import React, { useState } from 'react';
import { API_BASE_URL } from '../../config';
import { 
  Search, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Package, 
  Truck,
  Mail,
  MessageSquare,
  Calendar,
  User,
  MapPin,
  ShoppingBag,
  DollarSign,
  CreditCard,
  ArrowRight
} from 'lucide-react';
import LocationVisualization from '../LocationVisualization';

const OrderStatusWorkflow = () => {
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [notifications, setNotifications] = useState({ sms: [], email: [] });
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOrderWorkflow = async () => {
    if (!orderId || orderId.trim() === '') {
      alert('Please enter an order number or ID');
      return;
    }

    setLoading(true);
    setOrder(null);
    setStatusHistory([]);
    setNotifications({ sms: [], email: [] });
    setRefunds([]);
    
    try {
      const token = localStorage.getItem('adminToken');
      const searchTerm = orderId.trim();

      // First, try to search for the order by order number
      let orderToFetch = null;
      
      // Check if it looks like an order number (contains DPC- or similar pattern)
      if (searchTerm.includes('DPC-') || searchTerm.length > 10) {
        // Search by order number
        const searchResponse = await fetch(
          `${API_BASE_URL}/api/admin/orders?search=${encodeURIComponent(searchTerm)}&limit=1`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (searchResponse.ok) {
          const searchData = await searchResponse.json();
          const orders = searchData.orders || [];
          if (orders.length > 0) {
            orderToFetch = orders[0];
          }
        }
      }

      // If not found by search, try direct ID lookup
      if (!orderToFetch) {
        const orderResponse = await fetch(
          `${API_BASE_URL}/api/admin/orders/${searchTerm}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (orderResponse.ok) {
          orderToFetch = await orderResponse.json();
        } else if (orderResponse.status === 404) {
          alert('Order not found. Please check the order number or ID and try again.');
          setLoading(false);
          return;
        }
      }

      if (!orderToFetch) {
        alert('Order not found. Please check the order number or ID and try again.');
        setLoading(false);
        return;
      }

      // Always fetch full order details to ensure shipping data is included
      const orderIdToUse = orderToFetch.id;
      const fullOrderResponse = await fetch(
        `${API_BASE_URL}/api/admin/orders/${orderIdToUse}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (fullOrderResponse.ok) {
        const fullOrder = await fullOrderResponse.json();
        setOrder(fullOrder);
      } else {
        // Fallback to search result if full fetch fails
        setOrder(orderToFetch);
      }

      // Fetch status history
      const historyResponse = await fetch(
        `${API_BASE_URL}/api/admin/orders/${orderIdToUse}/status-history`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setStatusHistory(historyData.history || []);
      }

      // Fetch notifications
      const notificationsResponse = await fetch(
        `${API_BASE_URL}/api/admin/notifications/order/${orderIdToUse}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (notificationsResponse.ok) {
        const notificationsData = await notificationsResponse.json();
        setNotifications({
          sms: notificationsData.sms || [],
          email: notificationsData.email || []
        });
      }

      // Fetch refunds for this order
      const refundsResponse = await fetch(
        `${API_BASE_URL}/api/admin/refunds?orderId=${orderIdToUse}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (refundsResponse.ok) {
        const refundsData = await refundsResponse.json();
        setRefunds(refundsData.refunds || []);
      }
    } catch (error) {
      console.error('Error fetching order workflow:', error);
      alert('Failed to load order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'CONFIRMED':
        return <CheckCircle className="h-5 w-5 text-blue-500" />;
      case 'PROCESSING':
        return <Package className="h-5 w-5 text-orange-500" />;
      case 'SHIPPED':
        return <Truck className="h-5 w-5 text-purple-500" />;
      case 'DELIVERED':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'CANCELLED':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'REFUNDED':
        return <DollarSign className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800';
      case 'PROCESSING':
        return 'bg-orange-100 text-orange-800';
      case 'SHIPPED':
        return 'bg-purple-100 text-purple-800';
      case 'DELIVERED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'REFUNDED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `KES ${(amount || 0).toLocaleString()}`;
  };

  const getNotificationIcon = (type, status) => {
    const Icon = type === 'sms' ? MessageSquare : Mail;
    const color = status === 'SENT' || status === 'DELIVERED' ? 'text-green-500' : 
                  status === 'FAILED' ? 'text-red-500' : 'text-yellow-500';
    return <Icon className={`h-4 w-4 ${color}`} />;
  };

  const getRefundStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-blue-100 text-blue-800';
      case 'PROCESSING':
        return 'bg-orange-100 text-orange-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Parse location data if it's a JSON string
  const parseLocationData = (locationData) => {
    if (!locationData) return null;
    try {
      return typeof locationData === 'string' ? JSON.parse(locationData) : locationData;
    } catch {
      return null;
    }
  };

  // Parse coordinates if it's a JSON string
  const parseCoordinates = (coordinates) => {
    if (!coordinates) return null;
    try {
      return typeof coordinates === 'string' ? JSON.parse(coordinates) : coordinates;
    } catch {
      return null;
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Order Status Workflow</h2>

      {/* Order Search */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex space-x-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Enter order number or ID..."
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchOrderWorkflow()}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
            />
          </div>
          <button
            onClick={fetchOrderWorkflow}
            disabled={!orderId || loading}
            className="px-6 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] disabled:opacity-50 transition-colors"
          >
            {loading ? 'Loading...' : 'Load Order'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading order workflow...</div>
      ) : order ? (
        <div className="space-y-6">
          {/* Order Overview */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Order Overview
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Order Number</p>
                <p className="text-lg font-semibold">{order.orderNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Customer</p>
                <p className="text-lg font-semibold">{order.customer?.fullName || 'N/A'}</p>
                <p className="text-xs text-gray-500">{order.customer?.email || ''}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-lg font-semibold">
                  {formatCurrency(order.totalAmount || order.total || 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Current Status</p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                  {getStatusIcon(order.status)}
                  <span className="ml-2">{order.status}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Order Items */}
          {order.orderItems && order.orderItems.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <ShoppingBag className="h-5 w-5 mr-2" />
                Order Items ({order.orderItems.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {order.orderItems.map((item, index) => (
                      <tr key={item.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            {item.product?.image && (
                              <img 
                                src={item.product.image} 
                                alt={item.product.name}
                                className="h-12 w-12 object-cover rounded mr-3"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{item.product?.name || 'Unknown Product'}</p>
                              {item.product?.sku && (
                                <p className="text-xs text-gray-500">SKU: {item.product.sku}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {item.selectedColor ? (
                            <div className="flex items-center">
                              {item.selectedColorCode && (
                                <div 
                                  className="w-4 h-4 rounded-full mr-2 border border-gray-300"
                                  style={{ backgroundColor: item.selectedColorCode }}
                                />
                              )}
                              <span className="text-sm text-gray-700">{item.selectedColor}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium">{item.quantity}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-gray-900">{formatCurrency(item.price)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold text-gray-900">{formatCurrency(item.total || (item.price * item.quantity))}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="4" className="px-4 py-3 text-right font-medium text-gray-700">
                        Subtotal:
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {formatCurrency(order.subtotal || 0)}
                      </td>
                    </tr>
                    {order.shippingCost > 0 && (
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-right font-medium text-gray-700">
                          Shipping:
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          {formatCurrency(order.shippingCost)}
                        </td>
                      </tr>
                    )}
                    {order.tax > 0 && (
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-right font-medium text-gray-700">
                          Tax:
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          {formatCurrency(order.tax)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="4" className="px-4 py-3 text-right font-bold text-lg text-gray-900">
                        Total:
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-lg text-[#d44100]">
                        {formatCurrency(order.totalAmount || order.total || 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Delivery Location */}
          {(order.shipping || order.customer) && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Delivery Location
              </h3>
              
              {/* Use LocationVisualization component if coordinates or locationData available */}
              {(order.customer?.coordinates || order.customer?.locationData) ? (
                <LocationVisualization
                  locationData={parseLocationData(order.customer.locationData)}
                  coordinates={parseCoordinates(order.customer.coordinates)}
                  addressData={
                    order.shipping ? {
                      address: order.shipping.address,
                      city: order.shipping.city,
                      county: order.shipping.county,
                      country: order.shipping.country,
                      postalCode: order.shipping.postalCode
                    } : {
                      address: order.customer?.street,
                      city: order.customer?.city,
                      county: order.customer?.county,
                      country: order.customer?.country
                    }
                  }
                  view="card"
                  compact={false}
                />
              ) : (
                // Fallback to simple display if no coordinate data
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {order.shipping ? (
                    <>
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Address</p>
                        <p className="text-gray-900">{order.shipping.address}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">City</p>
                        <p className="text-gray-900">{order.shipping.city}</p>
                      </div>
                      {order.shipping.county && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">County</p>
                          <p className="text-gray-900">{order.shipping.county}</p>
                        </div>
                      )}
                      {order.shipping.postalCode && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Postal Code</p>
                          <p className="text-gray-900">{order.shipping.postalCode}</p>
                        </div>
                      )}
                      {order.shipping.country && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Country</p>
                          <p className="text-gray-900">{order.shipping.country}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {order.customer?.street && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Street</p>
                          <p className="text-gray-900">{order.customer.street}</p>
                        </div>
                      )}
                      {order.customer?.city && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">City</p>
                          <p className="text-gray-900">{order.customer.city}</p>
                        </div>
                      )}
                      {order.customer?.county && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">County</p>
                          <p className="text-gray-900">{order.customer.county}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Payment Information */}
          {order.payments && order.payments.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Payment Information
              </h3>
              <div className="space-y-3">
                {order.payments.map((payment, index) => (
                  <div key={payment.id || index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Method</p>
                        <p className="font-medium">{payment.method || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Amount</p>
                        <p className="font-medium">{formatCurrency(payment.amount)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                          payment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          payment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          payment.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {payment.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Date</p>
                        <p className="text-sm">{formatDate(payment.createdAt)}</p>
                      </div>
                    </div>
                    {payment.transactionId && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500">Transaction ID: {payment.transactionId}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Refund Information */}
          {refunds.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <DollarSign className="h-5 w-5 mr-2" />
                Refund History ({refunds.length})
              </h3>
              <div className="space-y-3">
                {refunds.map((refund, index) => (
                  <div key={refund.id || index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900">{refund.refundNumber}</p>
                        <p className="text-sm text-gray-500">Reason: {refund.reason}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRefundStatusColor(refund.status)}`}>
                        {refund.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-gray-600">Amount</p>
                        <p className="font-semibold text-gray-900">{formatCurrency(refund.amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Requested</p>
                        <p className="text-sm text-gray-700">{formatDate(refund.createdAt)}</p>
                      </div>
                      {refund.processedAt && (
                        <div>
                          <p className="text-xs text-gray-600">Processed</p>
                          <p className="text-sm text-gray-700">{formatDate(refund.processedAt)}</p>
                        </div>
                      )}
                      {refund.refundMethod && (
                        <div>
                          <p className="text-xs text-gray-600">Method</p>
                          <p className="text-sm text-gray-700">{refund.refundMethod}</p>
                        </div>
                      )}
                    </div>
                    {refund.description && (
                      <p className="text-sm text-gray-600 mt-2">{refund.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status Timeline */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Status History</h3>
            {statusHistory.length > 0 ? (
              <div className="relative">
                {statusHistory.map((entry, index) => (
                  <div key={entry.id} className="flex items-start mb-4">
                    <div className="flex flex-col items-center mr-4">
                      {getStatusIcon(entry.newStatus)}
                      {index < statusHistory.length - 1 && (
                        <div className="w-0.5 h-8 bg-gray-300 mt-2"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(entry.newStatus)}`}>
                          {entry.newStatus}
                        </span>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          from {entry.oldStatus}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {formatDate(entry.createdAt)}
                      </p>
                      {entry.notes && (
                        <p className="text-sm text-gray-500 mt-1">{entry.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No status history available</p>
            )}
          </div>

          {/* Notifications Timeline */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Notification History</h3>
            
            {notifications.sms.length === 0 && notifications.email.length === 0 ? (
              <p className="text-gray-500">No notifications sent for this order</p>
            ) : (
              <div className="space-y-4">
                {[...notifications.sms.map(n => ({ ...n, type: 'sms' })), 
                   ...notifications.email.map(n => ({ ...n, type: 'email' }))]
                  .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                  .map((notification) => (
                    <div key={notification.id} className="flex items-start border-l-2 border-gray-200 pl-4 py-2">
                      <div className="mr-3 mt-1">
                        {getNotificationIcon(notification.type, notification.status)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900 uppercase">
                            {notification.type}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            notification.status === 'SENT' || notification.status === 'DELIVERED'
                              ? 'bg-green-100 text-green-800'
                              : notification.status === 'FAILED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {notification.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.subject || notification.message?.substring(0, 60) + '...'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          To: {notification.recipient} | {formatDate(notification.sentAt || notification.createdAt)}
                        </p>
                        {notification.errorMessage && (
                          <p className="text-xs text-red-600 mt-1">
                            Error: {notification.errorMessage}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-800">{statusHistory.length}</p>
                <p className="text-sm text-gray-600">Status Changes</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{notifications.sms.length}</p>
                <p className="text-sm text-gray-600">SMS Sent</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{notifications.email.length}</p>
                <p className="text-sm text-gray-600">Emails Sent</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-600">{refunds.length}</p>
                <p className="text-sm text-gray-600">Refunds</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg shadow">
          <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p className="font-medium">Enter an order number to view its workflow</p>
          <p className="text-sm mt-1">Search by order number (e.g., DPC-123456) or order ID</p>
        </div>
      )}
    </div>
  );
};

export default OrderStatusWorkflow;
